/**
 * State of the planner assistant: whether the panel is open, the conversation,
 * and the "take me there" guide being played. Kept tiny and free of UI imports
 * because RootLayout reads it on every page to decide whether to load anything.
 */
import { create } from 'zustand';
import type { AttachmentKind } from './attachment';
import type { CatalogHit } from './catalogLookup';

export type AssistantScreen = 'designer' | 'designer-phone' | 'dashboard' | 'settings' | 'organization' | 'other';
export type AssistantStatus = 'available' | 'upgrade' | 'not_available' | 'unknown' | 'off_topic';

export interface AssistantItem {
  id: string;
  label: string;
  title: string;
  guide: string | null;
  /** "Do it" action (src/data/assistant/actions.ts), null when there is none. */
  action: string | null;
  minPlan: string | null;
  needsUpgrade: boolean;
}

export type AssistantMessage =
  | {
      role: 'user';
      text: string;
      /** A file sent with the question; kept in memory for "Add as floor plan". */
      attachment?: { name: string; kind: AttachmentKind; file: File };
    }
  | {
      role: 'assistant';
      text: string;
      id: string | null;
      status: AssistantStatus;
      items: AssistantItem[];
      featureRequest: string | null;
      /** Catalog models the reply names, shown as buttons that place them. */
      catalog?: CatalogHit[];
      /** The question it answers, sent along with a feature request. */
      question: string;
      /** The question asks for a change to the open project: the panel offers to do it. */
      doIt?: boolean;
      helpful?: boolean;
      requestSent?: boolean;
    }
  | { role: 'error'; text: string };

interface AssistantState {
  open: boolean;
  screen: AssistantScreen;
  messages: AssistantMessage[];
  pending: boolean;
  remaining: number | null;
  /** Guide being played, by id from src/data/assistant/guides.ts. */
  guideId: string | null;
  openPanel: (screen?: AssistantScreen) => void;
  closePanel: () => void;
  startGuide: (id: string) => void;
  endGuide: () => void;
  push: (m: AssistantMessage) => void;
  patchMessage: (index: number, patch: Partial<Extract<AssistantMessage, { role: 'assistant' }>>) => void;
  setPending: (v: boolean) => void;
  setRemaining: (n: number | null) => void;
  reset: () => void;
}

const GUIDE_KEY = 'cctvp_assistant_guide';
/** A guide left running survives page changes, not a coffee break. */
const GUIDE_TTL_MS = 10 * 60 * 1000;

function readGuide(): string | null {
  try {
    const raw = sessionStorage.getItem(GUIDE_KEY);
    if (!raw) return null;
    const { id, at } = JSON.parse(raw) as { id?: string; at?: number };
    if (typeof id !== 'string' || typeof at !== 'number' || Date.now() - at > GUIDE_TTL_MS) {
      sessionStorage.removeItem(GUIDE_KEY);
      return null;
    }
    return id;
  } catch {
    return null;
  }
}

function writeGuide(id: string | null) {
  try {
    if (id) sessionStorage.setItem(GUIDE_KEY, JSON.stringify({ id, at: Date.now() }));
    else sessionStorage.removeItem(GUIDE_KEY);
  } catch {
    /* private mode: the guide just won't survive a reload */
  }
}

export const useAssistantStore = create<AssistantState>((set) => ({
  open: false,
  screen: 'other',
  messages: [],
  pending: false,
  remaining: null,
  guideId: typeof window === 'undefined' ? null : readGuide(),
  openPanel: (screen) => set((s) => ({ open: true, screen: screen ?? s.screen })),
  closePanel: () => set({ open: false }),
  // Starting a guide hides the panel so the highlighted control is in view.
  startGuide: (id) => { writeGuide(id); set({ guideId: id, open: false }); },
  endGuide: () => { writeGuide(null); set({ guideId: null }); },
  push: (m) => set((s) => ({ messages: [...s.messages, m] })),
  patchMessage: (index, patch) => set((s) => ({
    messages: s.messages.map((m, i) => (i === index && m.role === 'assistant' ? { ...m, ...patch } : m)),
  })),
  setPending: (pending) => set({ pending }),
  setRemaining: (remaining) => set({ remaining }),
  reset: () => set({ messages: [], pending: false }),
}));

/** Open the assistant from anywhere (toolbar, menus, dashboard). */
export function openAssistant(screen?: AssistantScreen) {
  useAssistantStore.getState().openPanel(screen);
}
